/**
 * Hotbar bindings store — R232q5an.
 *
 * Persists which abilityId is mapped to each of the 12 main action bar
 * slots (keys 1..9, 0, -, =) per character class. WoW Sirus parity:
 *   – at level 1 only the first 1..3 abilities (those with minLevel <= 1)
 *     are auto-bound; the rest of the bar is empty until the player
 *     drags newly-learned spells from the Spellbook.
 *   – players can drag spells onto any slot, swap slots by drag, and
 *     right-click a slot to clear the binding.
 *
 * Storage key: `aiwari-hotbar-bindings-v1` -> { [classId]: (string|null)[12] }.
 *
 * NOTE: this is purely client state. Server still receives `useAbility`
 * by abilityId — it neither knows nor cares which slot was clicked.
 */

import { create } from 'zustand';
import { CLASSES, type ClassId } from '../data/classes.js';

const STORAGE_KEY = 'aiwari-hotbar-bindings-v1';
export const HOTBAR_SIZE = 12;

export type HotbarSlot = string | null;
export type HotbarRow = HotbarSlot[]; // length === HOTBAR_SIZE

interface HotbarStore {
  /** Per-class binding rows. Initialised lazily as classes become active. */
  bindings: Partial<Record<ClassId, HotbarRow>>;
  /**
   * Ensure a row exists for a class. If absent, populate slots 0..N-1
   * with the class's level-1 starter abilities (minLevel <= 1).
   * Subsequent calls are idempotent — won't overwrite an existing row.
   */
  ensureRow: (classId: ClassId) => void;
  /** Set ability into slot. Replaces whatever was there. */
  bind: (classId: ClassId, slotIdx: number, abilityId: string) => void;
  /** Clear slot. Used by right-click "Снять с панели". */
  clear: (classId: ClassId, slotIdx: number) => void;
  /** Swap two slot bindings inside the same row. */
  swap: (classId: ClassId, fromIdx: number, toIdx: number) => void;
  /** Read a class row (creates default if missing). */
  rowFor: (classId: ClassId) => HotbarRow;
}

function load(): Partial<Record<ClassId, HotbarRow>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<Record<ClassId, HotbarRow>>;
    // Sanity: ensure each row is exactly HOTBAR_SIZE long.
    const out: Partial<Record<ClassId, HotbarRow>> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (!Array.isArray(v)) continue;
      const fixed: HotbarRow = new Array(HOTBAR_SIZE).fill(null);
      for (let i = 0; i < HOTBAR_SIZE; i++) {
        const cell = v[i];
        fixed[i] = typeof cell === 'string' ? cell : null;
      }
      out[k as ClassId] = fixed;
    }
    return out;
  } catch {
    return {};
  }
}

function save(b: Partial<Record<ClassId, HotbarRow>>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(b)); } catch {}
}

function defaultRowFor(classId: ClassId): HotbarRow {
  const cls = CLASSES.find((c) => c.id === classId);
  const row: HotbarRow = new Array(HOTBAR_SIZE).fill(null);
  if (!cls) return row;
  // WoW Sirus parity: only level-1 starter abilities pre-bound.
  // (Sinister Strike for rogue, Firebolt for mage, Holy Strike + Smite for
  // paladin/cleric — anything with minLevel <= 1.)
  const starters = cls.abilities.filter((a) => a.minLevel <= 1);
  starters.slice(0, HOTBAR_SIZE).forEach((a, i) => { row[i] = a.id; });
  return row;
}

export const useHotbar = create<HotbarStore>((set, get) => ({
  bindings: load(),
  ensureRow: (classId) => {
    const cur = get().bindings;
    if (cur[classId]) return;
    const next = { ...cur, [classId]: defaultRowFor(classId) };
    save(next);
    set({ bindings: next });
  },
  bind: (classId, slotIdx, abilityId) => {
    if (slotIdx < 0 || slotIdx >= HOTBAR_SIZE) return;
    const cur = get().bindings;
    const row = cur[classId] ? [...cur[classId]!] : defaultRowFor(classId);
    // If ability is already bound elsewhere, swap it (avoid duplicates).
    const existingIdx = row.indexOf(abilityId);
    if (existingIdx >= 0 && existingIdx !== slotIdx) {
      row[existingIdx] = row[slotIdx];
    }
    row[slotIdx] = abilityId;
    const next = { ...cur, [classId]: row };
    save(next);
    set({ bindings: next });
  },
  clear: (classId, slotIdx) => {
    if (slotIdx < 0 || slotIdx >= HOTBAR_SIZE) return;
    const cur = get().bindings;
    const row = cur[classId] ? [...cur[classId]!] : defaultRowFor(classId);
    row[slotIdx] = null;
    const next = { ...cur, [classId]: row };
    save(next);
    set({ bindings: next });
  },
  swap: (classId, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    if (fromIdx < 0 || fromIdx >= HOTBAR_SIZE) return;
    if (toIdx < 0 || toIdx >= HOTBAR_SIZE) return;
    const cur = get().bindings;
    const row = cur[classId] ? [...cur[classId]!] : defaultRowFor(classId);
    [row[fromIdx], row[toIdx]] = [row[toIdx]!, row[fromIdx]!];
    const next = { ...cur, [classId]: row };
    save(next);
    set({ bindings: next });
  },
  rowFor: (classId) => {
    const cur = get().bindings;
    if (cur[classId]) return cur[classId]!;
    const fresh = defaultRowFor(classId);
    const next = { ...cur, [classId]: fresh };
    save(next);
    set({ bindings: next });
    return fresh;
  },
}));

/** Drag-payload mime: payload = JSON {abilityId, fromSlot?: number}. */
export const HOTBAR_DND_MIME = 'application/x-aiwari-ability';
